import React, { useState, useRef } from 'react';
import { Upload, FileText, Brain, CheckCircle, AlertCircle, Loader2, X, RefreshCw, FileUp, Zap, TrendingUp } from 'lucide-react';
import { GoogleGenerativeAI } from '@google/generative-ai';
import data from '../Database';
import * as pdfjsLib from 'pdfjs-dist/webpack';

const PDFUpload = ({ onMindmapGenerated, activeSessionId, setSessions, frameworkPhases, updateMindmap, pdfData }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [fileName, setFileName] = useState('');
  const [isCompleted, setIsCompleted] = useState(false);
  const [showNewUpload, setShowNewUpload] = useState(false);
  const [selectedModel, setSelectedModel] = useState('flash-lite'); // 'flash-lite' or 'pro'
  const fileInputRef = useRef(null);

  const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);

  const hasPdfData = pdfData && pdfData.trim().length > 0;

  // Model configurations
  const modelConfigs = {
    'flash-lite': {
      name: 'gemini-2.5-flash-lite',
      displayName: 'Flash Lite',
      description: 'Faster processing with good accuracy',
      icon: Zap,
      color: 'from-green-600 to-emerald-600',
      hoverColor: 'from-green-700 to-emerald-700'
    },
    'pro': {
      name: 'gemini-2.5-pro',
      displayName: 'Gemini Pro',
      description: 'Deeper analysis with enhanced reasoning',
      icon: TrendingUp,
      color: 'from-purple-600 to-blue-600',
      hoverColor: 'from-purple-700 to-blue-700'
    }
  };

  const extractTextFromPDF = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument(arrayBuffer).promise;
      let fullText = '';

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        fullText += pageText + '\n';

        setProgress((i / pdf.numPages) * 20);
      }

      console.log('Extracted text from PDF:', fullText);
      applyPDFData(fullText);
      return fullText;
    } catch (error) {
      console.error('Error extracting text from PDF:', error);
      throw new Error('Failed to extract text from PDF');
    }
  };

  // First Gemini call: select relevant items from available options
  const processPhaseWithGemini = async (pdfText, phaseName, phaseData, modelType) => {
    try {
      const modelConfig = modelConfigs[modelType];
      const model = genAI.getGenerativeModel({ model: modelConfig.name });

      const availableOptions = {
        abilities: phaseData.abilities || [],
        skills: [],
        knowledge: [],
        mindset: [],
        tools: []
      };

      Object.keys(phaseData).forEach(key => {
        if (typeof phaseData[key] === 'object' && phaseData[key] !== null && !Array.isArray(phaseData[key])) {
          const categoryData = phaseData[key];
          if (categoryData.skills) availableOptions.skills.push(...categoryData.skills);
          if (categoryData.knowledge) availableOptions.knowledge.push(...categoryData.knowledge);
          if (categoryData.mindset) availableOptions.mindset.push(...categoryData.mindset);
          if (categoryData.tools) availableOptions.tools.push(...categoryData.tools);
        }
      });

      const prompt = `
        You are analyzing a design project PDF for the ${phaseName.toUpperCase()} phase of the Design Thinking process.

        PDF Content:
        """
        ${pdfText}
        """

        Based on this PDF content, select ONLY the most relevant items from the provided lists below. DO NOT create new items or modify existing ones. Only select items that are explicitly listed.
        If the PDF content is short, select way fewer items. 
        maximum 2 abilities per phase.

        AVAILABLE OPTIONS FOR ${phaseName.toUpperCase()} PHASE:

        ABILITIES:
        ${availableOptions.abilities.map((item, index) => `${index + 1}. ${item}`).join('\n')}

        SKILLS:
        ${availableOptions.skills.map((item, index) => `${index + 1}. ${item}`).join('\n')}

        KNOWLEDGE:
        ${availableOptions.knowledge.map((item, index) => `${index + 1}. ${item}`).join('\n')}

        MINDSET:
        ${availableOptions.mindset.map((item, index) => `${index + 1}. ${item}`).join('\n')}

        TOOLS:
        ${availableOptions.tools.map((item, index) => `${index + 1}. ${item}`).join('\n')}

        INSTRUCTIONS:
        1. Read the PDF content carefully
        2. Select 0-2 most relevant items from each category above, judging by the length of the PDF document as well, determine how likely would the number of relevant items be.
            If document is short, more likely to be 0.
        3. Use EXACT text from the lists - do not modify or create new items
        4. Only select items that are truly relevant to the project described in the PDF, be conservative in your selections
        5. Return your selection in the exact JSON format below
        6. Make sure only 0-2 items are selected per category

        REQUIRED JSON FORMAT:
        {
          "abilities": ["exact ability text from list", "another exact ability text"],
          "skills": ["exact skill text from list", "another exact skill text"],
          "knowledge": ["exact knowledge text from list", "another exact knowledge text"],
          "mindset": ["exact mindset text from list", "another exact mindset text"],
          "tools": ["exact tool text from list", "another exact tool text"]
        }

        Return only valid JSON. Do not include any explanation or additional text.
      `;

      console.log(`Processing ${phaseName} phase with ${modelConfig.displayName}`);
      const result = await model.generateContent(prompt);
      const response = await result.response;
      const text = response.text();

      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error(`No valid JSON found in response for ${phaseName} phase`);
      }

      const parsedResponse = JSON.parse(jsonMatch[0]);

      // Validate: only keep items that exist in the original available options
      const validatedResponse = {
        abilities: parsedResponse.abilities?.filter(item => availableOptions.abilities.includes(item)) || [],
        skills: parsedResponse.skills?.filter(item => availableOptions.skills.includes(item)) || [],
        knowledge: parsedResponse.knowledge?.filter(item => availableOptions.knowledge.includes(item)) || [],
        mindset: parsedResponse.mindset?.filter(item => availableOptions.mindset.includes(item)) || [],
        tools: parsedResponse.tools?.filter(item => availableOptions.tools.includes(item)) || []
      };

      console.log(`Raw selection for ${phaseName} phase:`, validatedResponse);
      return { validatedResponse, availableOptions };
    } catch (error) {
      console.error(`Error processing ${phaseName} phase:`, error);
      throw new Error(`Failed to process ${phaseName} phase: ${error.message}`);
    }
  };

  // Second Gemini call: post-process and refine the initial selections
  const postProcessPhaseWithGemini = async (phaseName, initialSelection, availableOptions, modelType) => {
    try {
      const modelConfig = modelConfigs[modelType];
      const model = genAI.getGenerativeModel({ model: modelConfig.name });

      const prompt = `
        You are performing a post-processing refinement step for the ${phaseName.toUpperCase()} phase of a Design Thinking mindmap.

        Below is an initial selection of items made for this phase. Your job is to clean, trim, and enforce strict quality rules.

        INITIAL SELECTION:
        ${JSON.stringify(initialSelection, null, 2)}

        AVAILABLE OPTIONS (items must come from these lists only):

        ABILITIES:
        ${availableOptions.abilities.map((item, index) => `${index + 1}. ${item}`).join('\n')}

        SKILLS:
        ${availableOptions.skills.map((item, index) => `${index + 1}. ${item}`).join('\n')}

        KNOWLEDGE:
        ${availableOptions.knowledge.map((item, index) => `${index + 1}. ${item}`).join('\n')}

        MINDSET:
        ${availableOptions.mindset.map((item, index) => `${index + 1}. ${item}`).join('\n')}

        TOOLS:
        ${availableOptions.tools.map((item, index) => `${index + 1}. ${item}`).join('\n')}

        POST-PROCESSING RULES (apply all of these strictly):
        1. Remove 2 most irrelevant items across all abilities.
        2. If this phase does not seem relevant to the overall project context implied by the selections, return empty arrays for all categories.
        3. Each ability must have a maximum of 5 items total ,remove the least relevant if exceeded.
        4. Each category (skills, knowledge, mindset, tools) must have a maximum of 2 items — remove the least relevant if exceeded.
        5. The total number of items across ALL categories must not exceed 12 — trim from overrepresented categories first.
        6. Remove any item that does not EXACTLY match an entry in the AVAILABLE OPTIONS lists above.
        7. Be conservative , when in doubt, remove rather than keep.

        Return only valid JSON in this exact format. No explanation, no markdown, no extra text.

        {
          "abilities": [],
          "skills": [],
          "knowledge": [],
          "mindset": [],
          "tools": []
        }
      `;

      console.log(`Post-processing ${phaseName} phase with ${modelConfig.displayName}`);
      const result = await model.generateContent(prompt);
      const response = await result.response;
      const text = response.text();

      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error(`No valid JSON found in post-processing response for ${phaseName} phase`);
      }

      const parsedResponse = JSON.parse(jsonMatch[0]);

      // Final safety validation: only keep items that exist in availableOptions
      const finalResponse = {
        abilities: parsedResponse.abilities?.filter(item => availableOptions.abilities.includes(item)) || [],
        skills: parsedResponse.skills?.filter(item => availableOptions.skills.includes(item)) || [],
        knowledge: parsedResponse.knowledge?.filter(item => availableOptions.knowledge.includes(item)) || [],
        mindset: parsedResponse.mindset?.filter(item => availableOptions.mindset.includes(item)) || [],
        tools: parsedResponse.tools?.filter(item => availableOptions.tools.includes(item)) || []
      };

      console.log(`Post-processed ${phaseName} phase:`, finalResponse);
      return finalResponse;
    } catch (error) {
      console.error(`Error post-processing ${phaseName} phase:`, error);
      // Fall back to the initial selection if post-processing fails
      console.warn(`Falling back to initial selection for ${phaseName} phase`);
      return initialSelection;
    }
  };

  // Third layer: trim total category items to a max of 6 per ability
  // Since all category items (skills/knowledge/mindset/tools) are attached to EVERY ability node
  // in generateMindmapStructure, the combined count of those 4 arrays is what each ability "sees".
  // If that total exceeds 6, remove items from the largest category first (least relevant = last item).
  const trimCategoryItemsPerAbility = (refinedResult) => {
    const MAX_ITEMS_PER_ABILITY = 6;
    const categories = ['skills', 'knowledge', 'mindset', 'tools'];

    const totalItems = categories.reduce(
      (sum, cat) => sum + (refinedResult[cat]?.length || 0), 0
    );

    if (totalItems <= MAX_ITEMS_PER_ABILITY) return refinedResult;

    // Deep-clone the category arrays so we don't mutate the original
    const trimmed = {
      ...refinedResult,
      ...Object.fromEntries(categories.map(cat => [cat, [...(refinedResult[cat] || [])]]))
    };

    while (categories.reduce((sum, cat) => sum + trimmed[cat].length, 0) > MAX_ITEMS_PER_ABILITY) {
      // Find the category currently holding the most items and remove its last (least relevant) entry
      const largest = categories.reduce((a, b) => trimmed[a].length >= trimmed[b].length ? a : b);
      trimmed[largest].pop();
    }

    console.log(`Trimmed category items to max ${MAX_ITEMS_PER_ABILITY} per ability:`, trimmed);
    return trimmed;
  };

  const processAllPhases = async (pdfText, modelType) => {
    const phases = ['Empathy', 'Define', 'Ideate', 'Prototype', 'Test', 'Communicate'];
    const results = {};

    for (let i = 0; i < phases.length; i++) {
      const phaseName = phases[i];
      const phaseKey = phaseName.toLowerCase();

      // Step 1: Initial selection
      setUploadStatus(`Analyzing ${phaseName} phase...`);
      setProgress(20 + (i / phases.length) * 50);

      try {
        const phaseData = data[phaseName];
        if (!phaseData) {
          console.warn(`No data found for phase: ${phaseName}`);
          results[phaseKey] = {
            abilities: [],
            skills: [],
            knowledge: [],
            mindset: [],
            tools: []
          };
          continue;
        }

        const { validatedResponse, availableOptions } = await processPhaseWithGemini(pdfText, phaseName, phaseData, modelType);

        await new Promise(resolve => setTimeout(resolve, 500));

        // Step 2: Post-processing refinement
        setUploadStatus(`Refining ${phaseName} phase...`);
        setProgress(20 + (i / phases.length) * 50 + (1 / phases.length) * 25);

        const refinedResult = await postProcessPhaseWithGemini(phaseName, validatedResponse, availableOptions, modelType);

        // Step 3: Trim total category items to max 6 per ability
        results[phaseKey] = trimCategoryItemsPerAbility(refinedResult);

        await new Promise(resolve => setTimeout(resolve, 500));

      } catch (error) {
        console.error(`Error processing ${phaseName}:`, error);
        results[phaseKey] = {
          abilities: [],
          skills: [],
          knowledge: [],
          mindset: [],
          tools: []
        };
      }
    }

    return results;
  };

  const generateMindmapStructure = (phaseData, projectTitle = 'Design Thinking Project') => {
    const nodes = [];
    const edges = [];

    nodes.push({
      id: 'center',
      label: projectTitle,
      x: 400,
      y: 300,
      type: 'center',
      color: '#e3daeb'
    });

    const phases = Object.keys(phaseData);
    const radius = 150;

    phases.forEach((phase, phaseIndex) => {
      const phaseAngle = (phaseIndex * 2 * Math.PI) / phases.length;
      const phaseX = 400 + Math.cos(phaseAngle) * radius;
      const phaseY = 300 + Math.sin(phaseAngle) * radius;

      const phaseId = `phase_${phase}`;
      nodes.push({
        id: phaseId,
        label: phase.charAt(0).toUpperCase() + phase.slice(1),
        x: phaseX,
        y: phaseY,
        type: 'phase',
        color: '#e3daeb'
      });

      edges.push({
        id: `center_to_${phase}`,
        source: 'center',
        target: phaseId
      });

      const abilities = phaseData[phase].abilities || [];
      abilities.forEach((ability, abilityIndex) => {
        const abilityAngle = phaseAngle + (abilityIndex - (abilities.length - 1) / 2) * 0.5;
        const abilityRadius = 80;
        const abilityX = phaseX + Math.cos(abilityAngle) * abilityRadius;
        const abilityY = phaseY + Math.sin(abilityAngle) * abilityRadius;

        const abilityId = `${phase}_ability_${abilityIndex}`;
        nodes.push({
          id: abilityId,
          label: ability,
          x: abilityX,
          y: abilityY,
          type: 'ability',
          color: '#e3daeb'
        });

        edges.push({
          id: `${phaseId}_to_${abilityId}`,
          source: phaseId,
          target: abilityId
        });

        const categories = ['skills', 'knowledge', 'mindset', 'tools'];
        const colors = {
          skills: '#62BB67',
          knowledge: '#378FC0',
          mindset: '#E4A204',
          tools: '#7C1586'
        };

        categories.forEach((category, categoryIndex) => {
          const categoryItems = phaseData[phase][category] || [];
          if (categoryItems.length > 0) {
            categoryItems.forEach((item, itemIndex) => {
              const itemAngle = abilityAngle + (categoryIndex - 1.5) * 0.3 + (itemIndex - (categoryItems.length - 1) / 2) * 0.1;
              const itemRadius = 60;
              const itemX = abilityX + Math.cos(itemAngle) * itemRadius;
              const itemY = abilityY + Math.sin(itemAngle) * itemRadius;

              const itemId = `${abilityId}_${category}_${itemIndex}`;
              nodes.push({
                id: itemId,
                label: item,
                x: itemX,
                y: itemY,
                type: category,
                color: colors[category],
                size: 'small'
              });

              edges.push({
                id: `${abilityId}_to_${itemId}`,
                source: abilityId,
                target: itemId
              });
            });
          }
        });
      });
    });

    return { nodes, edges };
  };

  const applyMindmapData = (processedData) => {
    setSessions(prevSessions => prevSessions.map(session => {
      if (session.id === activeSessionId) {
        const updatedMindmap = { ...session.mindmap };

        updatedMindmap.phaseData = processedData;

        const { nodes, edges } = generateMindmapStructure(processedData, 'PDF Analysis Project');

        updatedMindmap.nodes = nodes;
        updatedMindmap.edges = edges;

        return {
          ...session,
          mindmap: updatedMindmap,
          mindmapGenerated: true
        };
      }
      return session;
    }));
  };

  const applyPDFData = (pdfData) => {
    setSessions(prevSessions => prevSessions.map(session => {
      if (session.id === activeSessionId) {
        return {
          ...session,
          pdfData: pdfData
        };
      }
      return session;
    }));
  };

  const applyRegeneratedMindmap = () => {
    setSessions(prevSessions => prevSessions.map(session => {
      if (session.id === activeSessionId) {
        return {
          ...session,
          mindmapRegenarated: true
        };
      }
      return session;
    }));
  };

  const handleRegenerate = async () => {
    setIsProcessing(true);
    setUploadStatus('Regenerating mindmap...');
    setProgress(0);
    setIsCompleted(false);

    try {
      setUploadStatus('Using existing PDF data...');
      setProgress(20);

      if (!pdfData || !pdfData.trim()) {
        throw new Error('No PDF data available for regeneration');
      }

      const processedData = await processAllPhases(pdfData, selectedModel);

      setUploadStatus('Generating mindmap...');
      setProgress(90);

      applyMindmapData(processedData);
      applyRegeneratedMindmap();

      setUploadStatus('Mindmap regenerated successfully!');
      setProgress(100);
      setIsCompleted(true);

      if (onMindmapGenerated) {
        onMindmapGenerated({
          analysis: `Successfully regenerated mindmap using ${modelConfigs[selectedModel].displayName}.`,
          mindmapData: processedData,
          recommendations: ["Review each phase carefully", "Validate selections against project requirements", "Consider additional customization as needed"]
        });
      }

      console.log('Regenerated mindmap data:', processedData);
      setIsProcessing(false);

    } catch (error) {
      console.error('Error regenerating mindmap:', error);
      setUploadStatus(`Error: ${error.message}`);
      setProgress(0);
      setIsProcessing(false);
      setTimeout(() => {
        setUploadStatus('');
      }, 5000);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type === 'application/pdf') {
        handleFileProcessing(file);
      } else {
        setUploadStatus('Please select a valid PDF file');
        setTimeout(() => setUploadStatus(''), 3000);
      }
    }
  };

  const handleFileProcessing = async (file) => {
    if (!file || file.type !== 'application/pdf') {
      setUploadStatus('Please select a valid PDF file');
      setTimeout(() => setUploadStatus(''), 3000);
      return;
    }

    setFileName(file.name);
    setIsProcessing(true);
    setUploadStatus('Processing PDF...');
    setProgress(0);
    setIsCompleted(false);
    setShowNewUpload(false);

    try {
      setUploadStatus('Extracting text from PDF...');
      const pdfText = await extractTextFromPDF(file);

      if (!pdfText.trim()) {
        throw new Error('No text found in PDF');
      }

      const processedData = await processAllPhases(pdfText, selectedModel);

      setUploadStatus('Generating mindmap...');
      setProgress(90);

      applyMindmapData(processedData);

      setUploadStatus('Mindmap generated successfully!');
      setProgress(100);
      setIsCompleted(true);

      if (onMindmapGenerated) {
        onMindmapGenerated({
          analysis: `Successfully processed PDF with ${modelConfigs[selectedModel].displayName}.`,
          mindmapData: processedData,
          recommendations: ["Review each phase carefully", "Validate selections against project requirements", "Consider additional customization as needed"]
        });
      }

      console.log('Mindmap data:', processedData);
      setIsProcessing(false);

    } catch (error) {
      console.error('Error processing PDF:', error);
      setUploadStatus(`Error: ${error.message}`);
      setProgress(0);
      setIsProcessing(false);
      setTimeout(() => {
        setUploadStatus('');
        setFileName('');
      }, 5000);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileProcessing(file);
    }
  };

  const clearFile = () => {
    setFileName('');
    setUploadStatus('');
    setProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getStatusIcon = () => {
    if (isProcessing) return <Loader2 className="w-5 h-5 animate-spin text-blue-500" />;
    if (uploadStatus.includes('Error')) return <AlertCircle className="w-5 h-5 text-red-500" />;
    if (uploadStatus.includes('successfully')) return <CheckCircle className="w-5 h-5 text-[#62BB67]" />;
    return null;
  };

  // Model Selection Component
  const ModelSelector = () => (
    <div className="mb-6">
      <h3 className="text-sm font-semibold text-gray-500 mb-3 text-center">Select AI Model</h3>
      <div className="grid grid-cols-2 gap-4">
        {Object.entries(modelConfigs).map(([key, config]) => {
          const Icon = config.icon;
          return (
            <button
              key={key}
              onClick={() => setSelectedModel(key)}
              disabled={isProcessing}
              className={`relative p-4 rounded-lg border-2 transition-all duration-200 ${
                selectedModel === key
                  ? 'border-[#380A64] bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              } ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div className="flex flex-col items-center text-center">
                <Icon className={`w-6 h-6 mb-2 ${selectedModel === key ? 'text-[#380A64]' : 'text-gray-600'}`} />
                <span className={`font-semibold text-sm mb-1 ${selectedModel === key ? 'text-[#380A64]' : 'text-gray-800'}`}>
                  {config.displayName}
                </span>
                <span className="text-xs text-gray-600">{config.description}</span>
              </div>
              {selectedModel === key && (
                <div className="absolute top-2 right-2">
                  <CheckCircle className="w-5 h-5 text-[#380A64]" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  // Progress indicator showing both analysis and refinement steps
  const PhaseProgressGrid = () => (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
      {['Empathy', 'Define', 'Ideate', 'Prototype', 'Test', 'Communicate'].map((phase, index) => {
        const analyzeThreshold = 20 + (index * 50 / 6);
        const refineThreshold = 20 + (index * 50 / 6) + (50 / 6) * 0.5;
        const done = progress > analyzeThreshold + (50 / 6);
        const refining = !done && progress > refineThreshold;
        const analyzing = !done && !refining && progress > analyzeThreshold;

        return (
          <div
            key={phase}
            className={`flex items-center space-x-2 p-2 rounded ${
              done      ? 'bg-green-100 text-green-700' :
              refining  ? 'bg-purple-100 text-purple-700' :
              analyzing ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-500'
            }`}
          >
            <div className={`w-2 h-2 rounded-full ${
              done      ? 'bg-[#62BB67]' :
              refining  ? 'bg-purple-500 animate-pulse' :
              analyzing ? 'bg-blue-500 animate-pulse' :
                          'bg-gray-300'
            }`} />
            <span>
              {phase}
              {refining  && <span className="ml-1 opacity-60">(refining)</span>}
              {analyzing && <span className="ml-1 opacity-60">(analyzing)</span>}
            </span>
          </div>
        );
      })}
    </div>
  );

  if (hasPdfData && !showNewUpload && !isProcessing) {
    return (
      <div className="w-full max-w-2xl mx-auto p-6 rounded-xl border-gray-200">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2 mt-12">
            Dittrium AI Mindmap Generator
          </h2>
          <p className="text-gray-600">
            {isCompleted ? 'Your mindmap has been generated successfully!' : 'PDF data is available - regenerate or upload a new file'}
          </p>
        </div>

        {isCompleted ? (
          <div className="text-center py-12">
            <div className="flex items-center justify-center mb-6">
              <div className="p-4 bg-green-100 rounded-full">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
            </div>
            <p className="text-gray-600 mb-6">
              Your mindmap has been regenerated using the existing PDF data with fresh AI analysis.
            </p>
          </div>
        ) : (
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <div className="p-4 bg-blue-100 rounded-full">
                <Brain className="w-12 h-12 text-blue-600" />
              </div>
            </div>

            <ModelSelector />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-lg mx-auto">
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-6 border border-purple-200">
                <div className="flex items-center justify-center mb-4">
                  <RefreshCw className="w-8 h-8 text-[#380A64]" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">Regenerate Mindmap</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Use existing PDF data with fresh AI analysis to generate a new mindmap variation.
                </p>
                <button
                  onClick={handleRegenerate}
                  className={`w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r ${modelConfigs[selectedModel].color} hover:${modelConfigs[selectedModel].hoverColor} transition-all duration-200`}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerate
                </button>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-6 border border-green-200">
                <div className="flex items-center justify-center mb-4">
                  <FileUp className="w-8 h-8 text-green-600" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">Upload New PDF</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Start fresh with a completely new PDF document and generate a new mindmap.
                </p>
                <button
                  onClick={() => setShowNewUpload(true)}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 transition-all duration-200"
                >
                  <FileUp className="w-4 h-4 mr-2" />
                  New Upload
                </button>
              </div>
            </div>

            <p className="text-xs pl-5 pt-4 font-bold text-gray-300">© All rights reserved <span className="text-xs font-normal font-ethnocentric">21st Century Edutech LLC</span></p>
          </div>
        )}

        {(uploadStatus || isProcessing) && (
          <div className="mt-6 space-y-4">
            <div className="flex items-center justify-center space-x-3 p-4 bg-gray-50 rounded-lg">
              {getStatusIcon()}
              <span className={`text-sm font-medium ${
                uploadStatus.includes('Error') ? 'text-red-600' :
                uploadStatus.includes('successfully') ? 'text-green-600' :
                'text-blue-600'
              }`}>
                {uploadStatus}
              </span>
            </div>

            {isProcessing && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Processing...</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-[#380A64] to-blue-500 h-2 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}

            {isProcessing && <PhaseProgressGrid />}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto p-6 rounded-xl border-gray-200">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2 mt-12">
          Dittrium AI Mindmap Generator
        </h2>
        <p className="text-gray-600">
          {isCompleted ? 'Your mindmap has been generated successfully!' : 'Upload your PDF to automatically generate a comprehensive design thinking mindmap'}
        </p>
      </div>

      {hasPdfData && showNewUpload && !isProcessing && (
        <div className="mb-4">
          <button
            onClick={() => setShowNewUpload(false)}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-800 transition-colors duration-200"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to regenerate options
          </button>
        </div>
      )}

      {isCompleted ? (
        <div className="text-center py-12">
          <div className="flex items-center justify-center mb-6">
            <div className="p-4 bg-green-100 rounded-full">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
          </div>

          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            Mindmap Generated Successfully!
          </h3>

          <p className="text-gray-600 mb-6">
            Your PDF "{fileName}" has been analyzed and converted into a comprehensive design thinking mindmap.
          </p>

          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 mb-6">
            <h4 className="font-semibold text-gray-800 mb-3">Generated Content:</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              {['Empathy', 'Define', 'Ideate', 'Prototype', 'Test', 'Communicate'].map((phase) => (
                <div key={phase} className="flex items-center space-x-2 text-gray-700">
                  <div className="w-2 h-2 rounded-full bg-[62BB67]" />
                  <span>{phase} Phase</span>
                </div>
              ))}
            </div>
          </div>

          <div className="text-sm text-gray-600 space-y-2">
            <p>✓ AI analysis completed for all 6 design thinking phases</p>
            <p>✓ Post-processing refinement applied for quality assurance</p>
            <p>✓ Category items trimmed to max 6 per ability node</p>
            <p>✓ Skills, knowledge, mindset, and tools categorized</p>
            <p>✓ Interactive mindmap nodes and connections generated</p>
          </div>
        </div>
      ) : (
        <>
          <ModelSelector />

          <div
            className={`relative border-2 border-dashed rounded-lg p-8 transition-all duration-300 ${
              dragActive
                ? 'border-blue-500 bg-blue-50'
                : isProcessing
                ? 'border-gray-300 bg-gray-50'
                : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              onChange={handleFileUpload}
              disabled={isProcessing}
              className="hidden"
              id="pdf-upload"
            />

            <div className="text-center">
              {fileName ? (
                <div className="mb-4">
                  <div className="flex items-center justify-center mb-2">
                    <FileText className="w-12 h-12 text-blue-500" />
                  </div>
                  <p className="text-sm font-medium text-gray-800">{fileName}</p>
                  <button
                    onClick={clearFile}
                    disabled={isProcessing}
                    className="mt-2 text-sm text-gray-500 hover:text-red-500 transition-colors duration-200 disabled:opacity-50"
                  >
                    <X className="w-4 h-4 inline mr-1" />
                    Clear
                  </button>
                </div>
              ) : (
                <div className="mb-4">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-lg font-medium text-gray-700 mb-2">
                    {dragActive ? 'Drop your PDF here' : 'Upload PDF Document'}
                  </p>
                  <p className="text-sm text-gray-500">
                    Drag and drop your PDF file or click to browse
                  </p>
                </div>
              )}

              {!fileName && (
                <label
                  htmlFor="pdf-upload"
                  className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-[62BB67] cursor-pointer transition-all duration-200 ${
                    isProcessing ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <Upload className="w-5 h-5 mr-2" />
                  Choose PDF File
                </label>
              )}
            </div>
          </div>

          {(uploadStatus || isProcessing) && (
            <div className="mt-6 space-y-4">
              <div className="flex items-center justify-center space-x-3 p-4 bg-gray-50 rounded-lg">
                {getStatusIcon()}
                <span className={`text-sm font-medium ${
                  uploadStatus.includes('Error') ? 'text-red-600' :
                  uploadStatus.includes('successfully') ? 'text-green-600' :
                  'text-blue-600'
                }`}>
                  {uploadStatus}
                </span>
              </div>

              {isProcessing && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Processing...</span>
                    <span>{Math.round(progress)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-[#380A64] to-[#378FC0] h-2 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}

              {isProcessing && <PhaseProgressGrid />}
            </div>
          )}

          <div className="mt-8 grid grid-cols-1 md:grid-rows-2 text-justify gap-4 text-sm">
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">How Dittrium AI Works</h4>
              <p className="text-gray-600 mb-3">
                Dittrium AI takes your existing PDF documents and intelligently converts them into comprehensive design thinking competency maps. Our advanced AI analyzes your content and automatically structures it into actionable learning pathways.
              </p>
              <p className="text-gray-600">
                Simply upload your PDF - whether it's a course curriculum, training manual, or knowledge base - and watch as Dittrium AI identifies key skills, knowledge areas, mindsets and instruments, then organizes them into an interactive mindmap for enhanced learning and development.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Comprehensive Analysis</h4>
              <p className="text-gray-600 mb-3">
                Our AI ensures your PDF content is thoroughly analyzed and nothing is missed. It identifies skills, knowledge areas, mindsets, and instruments mentioned in your document, creating a complete competency framework.
              </p>
              <p className="text-gray-600">
                The resulting visualization helps you understand the full scope of your content, identify learning paths, and discover connections between different concepts that might not be immediately obvious in the original document format.
              </p>
            </div>
            <p className="text-xs font-bold text-gray-500 mx-auto">© 2025 All rights reserved <span className="text-xs font-normal font-ethnocentric">21st Century Edutech LLC</span>.</p>
          </div>
        </>
      )}
    </div>
  );
};

export default PDFUpload;